import React, { useState, useEffect, useCallback } from 'react'; 
import Head from 'next/head';
import CalculatorQuoteRequest from "@/components/tools/CalculatorQuoteRequest";
import { motion } from 'framer-motion';
import { 
  Calculator, 
  Car, 
  Settings, 
  Info, 
  Target, 
  Ruler,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  Search,
  Filter
} from 'lucide-react';
import DownloadApp from '@/components/shared/DownloadApp';
import dynamic from 'next/dynamic';
import { usePopup } from '@/contexts/PopupContext';
import { ShoppingCart } from 'lucide-react';
import Link from 'next/link';
import { getFullUrl } from "@/lib/utils";

// Add the dynamic import
const RequestItemFormPopup = dynamic(() => import("@/components/requestItem/RequestItemFormPopup"), {
  ssr: false,
});

interface PCDResult {
  pcd: number;
  isValid: boolean;
  formula: string;
}

interface VehicleData {
  make: string;
  model: string;
  year: string;
  pcd: string;
  centerBore: string;
  wheelSize: string;
  category: 'passenger' | 'commercial' | 'suv' | 'luxury';
}

const WheelPCDCalculator: React.FC = () => {
  const [holes, setHoles] = useState<number>(5);
  const [measurement, setMeasurement] = useState<string>('');
  const [measurementType, setMeasurementType] = useState<'center-to-center' | 'edge-to-edge'>('center-to-center');
  const [result, setResult] = useState<PCDResult | null>(null);
  const [activeTab, setActiveTab] = useState<'calculator' | 'popular-sizes' | 'vehicle-data'>('calculator');
  const [vehicleTab, setVehicleTab] = useState<'passenger' | 'commercial' | 'suv' | 'luxury'>('passenger');
  const [searchTerm, setSearchTerm] = useState('');

   // Add popup context
  const { 
    isPopupOpen, 
    popupType, 
    resetPopup,
    setPopupOpen,
    setPopupType
  } = usePopup();

  // Add local state as backup
  const [localShowPopup, setLocalShowPopup] = useState(false);

  // Add popup handling functions
  const handleWheelQuoteRequest = (quoteType: string, wheelData?: any) => {
    try {
      console.log('=== WHEEL QUOTE REQUEST CLICKED ===');
      console.log('Quote Type:', quoteType);
      
      // Include current PCD calculations and vehicle data
      const wheelInfo = {
        pcdCalculation: result ? {
          pcd: result.pcd,
          holes: holes,
          formula: result.formula,
          measurementType: measurementType,
          measurement: measurement
        } : null,
        searchCriteria: {
          category: vehicleTab,
          searchTerm: searchTerm || undefined
        },
        wheelRequirements: result ? `${holes}x${result.pcd}` : `${holes} bolt pattern`,
        ...wheelData
      };

      // Set initial data for the wheel quote request
      const serviceData = {
        title: result 
          ? `Wheel Quotes for ${holes}x${result.pcd} PCD`
          : 'Custom Wheel / Mag Quote Request',
        description: result 
          ? `Get competitive quotes for wheels/mags with ${holes}x${result.pcd} bolt pattern. Calculated PCD: ${result.pcd}mm using ${measurementType} measurement method.`
          : 'Get competitive quotes for custom wheels and mags from verified suppliers. Professional wheel fitment consultation included.',
        serviceId: 'wheel-quotes',
        categoryId: 'wheels-tyres',
        subCategoryId: 'custom-wheels',
        domainCategoryId: 'automotive',
        wheelPCDData: wheelInfo
      };

      // Store in sessionStorage
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('selectedServiceData', JSON.stringify(serviceData));
      }

      // Open popup using available methods
      if (typeof setPopupOpen === 'function' && typeof setPopupType === 'function') {
        console.log('Using context methods: setPopupType + setPopupOpen');
        setPopupType('carousel');
        setPopupOpen(true);
      } else {
        console.log('Using local state fallback');
        setLocalShowPopup(true);
      }
      
    } catch (error) {
      console.error('Error in handleWheelQuoteRequest:', error);
    }
  };

  // Add popup close handler
  const handleClosePopup = () => {
    try {
      console.log('=== CLOSING POPUP ===');
      
      if (typeof resetPopup === 'function') {
        resetPopup();
      }
      
      if (typeof setPopupOpen === 'function') {
        setPopupOpen(false);
      }
      
      setLocalShowPopup(false);
      
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('selectedServiceData');
      }
    } catch (error) {
      console.error('Error in handleClosePopup:', error);
    }
  };

  // Add getInitialData function
  const getInitialData = useCallback(() => {
    if (typeof window !== 'undefined') {
      const storedData = sessionStorage.getItem('selectedServiceData');
      if (storedData) {
        try {
          return JSON.parse(storedData);
        } catch (e) {
          console.error('Error parsing stored service data:', e);
        }
      }
    }
    return undefined;
  }, []);

  // Popular wheel sizes and their common PCD values
  const popularSizes = [
    { size: '13"', commonPCD: ['4x100', '4x108'], centerBore: '57.1-63.4mm' },
    { size: '14"', commonPCD: ['4x100', '4x108', '5x100'], centerBore: '57.1-65.1mm' },
    { size: '15"', commonPCD: ['4x100', '4x108', '5x100', '5x112'], centerBore: '57.1-66.6mm' },
    { size: '16"', commonPCD: ['5x100', '5x108', '5x112', '5x114.3'], centerBore: '63.4-72.6mm' },
    { size: '17"', commonPCD: ['5x108', '5x112', '5x114.3', '5x120'], centerBore: '63.4-74.1mm' },
    { size: '18"', commonPCD: ['5x112', '5x114.3', '5x120', '5x130'], centerBore: '66.6-84.1mm' },
    { size: '19"', commonPCD: ['5x112', '5x114.3', '5x120', '5x130'], centerBore: '66.6-84.1mm' },
    { size: '20"', commonPCD: ['5x112', '5x114.3', '5x120', '5x130'], centerBore: '72.6-84.1mm' },
    { size: '22"', commonPCD: ['5x114.3', '5x120', '5x127', '5x130'], centerBore: '74.1-87.1mm' }
  ];

  // South African vehicle PCD database
  const vehicleData: VehicleData[] = [
    // Passenger Cars
    { make: 'Toyota', model: 'Corolla', year: '2019-2024', pcd: '5x100', centerBore: '54.1mm', wheelSize: '15"-17"', category: 'passenger' },
    { make: 'Toyota', model: 'Camry', year: '2018-2024', pcd: '5x114.3', centerBore: '60.1mm', wheelSize: '16"-18"', category: 'passenger' },
    { make: 'Volkswagen', model: 'Polo', year: '2018-2024', pcd: '5x100', centerBore: '57.1mm', wheelSize: '14"-16"', category: 'passenger' },
    { make: 'Volkswagen', model: 'Golf', year: '2019-2024', pcd: '5x112', centerBore: '57.1mm', wheelSize: '16"-18"', category: 'passenger' },
    { make: 'Honda', model: 'Civic', year: '2016-2024', pcd: '5x114.3', centerBore: '64.1mm', wheelSize: '16"-18"', category: 'passenger' },
    { make: 'Nissan', model: 'Almera', year: '2020-2024', pcd: '4x100', centerBore: '59.1mm', wheelSize: '15"-16"', category: 'passenger' },
    { make: 'Hyundai', model: 'Accent', year: '2018-2024', pcd: '4x100', centerBore: '54.1mm', wheelSize: '14"-16"', category: 'passenger' },
    { make: 'Kia', model: 'Rio', year: '2017-2024', pcd: '4x100', centerBore: '54.1mm', wheelSize: '14"-16"', category: 'passenger' },
    
    // SUVs
    { make: 'Toyota', model: 'Fortuner', year: '2016-2024', pcd: '6x139.7', centerBore: '106.1mm', wheelSize: '17"-20"', category: 'suv' },
    { make: 'Toyota', model: 'RAV4', year: '2019-2024', pcd: '5x114.3', centerBore: '60.1mm', wheelSize: '17"-19"', category: 'suv' },
    { make: 'Ford', model: 'EcoSport', year: '2018-2024', pcd: '4x108', centerBore: '63.4mm', wheelSize: '16"-17"', category: 'suv' },
    { make: 'Ford', model: 'Everest', year: '2015-2024', pcd: '6x139.7', centerBore: '93.1mm', wheelSize: '18"-20"', category: 'suv' },
    { make: 'Volkswagen', model: 'Tiguan', year: '2017-2024', pcd: '5x112', centerBore: '57.1mm', wheelSize: '17"-19"', category: 'suv' },
    { make: 'Hyundai', model: 'Tucson', year: '2016-2024', pcd: '5x114.3', centerBore: '67.1mm', wheelSize: '17"-19"', category: 'suv' },
    { make: 'Kia', model: 'Sportage', year: '2016-2024', pcd: '5x114.3', centerBore: '67.1mm', wheelSize: '17"-19"', category: 'suv' },
    { make: 'Mazda', model: 'CX-5', year: '2017-2024', pcd: '5x114.3', centerBore: '67.1mm', wheelSize: '17"-19"', category: 'suv' },

    // Commercial Vehicles
    { make: 'Toyota', model: 'Hilux', year: '2016-2024', pcd: '6x139.7', centerBore: '106.1mm', wheelSize: '16"-18"', category: 'commercial' },
    { make: 'Ford', model: 'Ranger', year: '2012-2024', pcd: '6x139.7', centerBore: '93.1mm', wheelSize: '16"-20"', category: 'commercial' },
    { make: 'Isuzu', model: 'D-Max', year: '2017-2024', pcd: '6x139.7', centerBore: '108mm', wheelSize: '16"-18"', category: 'commercial' },
    { make: 'Nissan', model: 'Navara', year: '2015-2024', pcd: '6x114.3', centerBore: '66.1mm', wheelSize: '16"-18"', category: 'commercial' },
    { make: 'Volkswagen', model: 'Amarok', year: '2017-2024', pcd: '5x120', centerBore: '65.1mm', wheelSize: '17"-20"', category: 'commercial' },
    { make: 'Mahindra', model: 'Pik Up', year: '2017-2024', pcd: '6x139.7', centerBore: '108mm', wheelSize: '15"-17"', category: 'commercial' },

    // Luxury Cars
    { make: 'BMW', model: '3 Series', year: '2019-2024', pcd: '5x120', centerBore: '72.6mm', wheelSize: '17"-19"', category: 'luxury' },
    { make: 'BMW', model: 'X3', year: '2018-2024', pcd: '5x120', centerBore: '72.6mm', wheelSize: '18"-20"', category: 'luxury' },
    { make: 'Mercedes-Benz', model: 'C-Class', year: '2018-2024', pcd: '5x112', centerBore: '66.6mm', wheelSize: '17"-19"', category: 'luxury' },
    { make: 'Mercedes-Benz', model: 'GLC', year: '2016-2024', pcd: '5x112', centerBore: '66.6mm', wheelSize: '18"-20"', category: 'luxury' },
    { make: 'Audi', model: 'A4', year: '2016-2024', pcd: '5x112', centerBore: '57.1mm', wheelSize: '17"-19"', category: 'luxury' },
    { make: 'Audi', model: 'Q5', year: '2017-2024', pcd: '5x112', centerBore: '57.1mm', wheelSize: '18"-20"', category: 'luxury' },
    { make: 'Lexus', model: 'ES', year: '2018-2024', pcd: '5x114.3', centerBore: '60.1mm', wheelSize: '17"-18"', category: 'luxury' },
    { make: 'Volvo', model: 'XC60', year: '2017-2024', pcd: '5x108', centerBore: '63.4mm', wheelSize: '18"-20"', category: 'luxury' }
  ];

  const calculatePCD = () => {
    if (!measurement || holes < 3) {
      setResult(null);
      return;
    }

    const dist = parseFloat(measurement);
    if (isNaN(dist) || dist <= 0) {
      setResult(null);
      return;
    }

    let pcd: number;
    let formula: string;

    if (measurementType === 'center-to-center') {
      // For center-to-center measurement across the circle
      if (holes % 2 === 0) {
        // Even number of holes - measurement is across the diameter
        pcd = dist;
        formula = `PCD = ${dist}mm (direct measurement across center)`;
      } else {
        // Odd number of holes - use trigonometry
        const angle = 360 / holes;
        pcd = dist / Math.cos((angle * Math.PI) / 360);
        formula = `PCD = ${dist} ÷ cos(${angle/2}°) = ${pcd.toFixed(1)}mm`;
      }
    } else {
      // Edge-to-edge measurement
      const angleRad = Math.PI / holes;
      pcd = dist / Math.sin(angleRad);
      formula = `PCD = ${dist} ÷ sin(180°/${holes}) = ${pcd.toFixed(1)}mm`;
    }

    setResult({
      pcd: Math.round(pcd * 10) / 10,
      isValid: pcd > 0 && pcd < 1000,
      formula
    });
  };

  useEffect(() => {
    calculatePCD();
  }, [holes, measurement, measurementType]);

  const filteredVehicles = vehicleData
    .filter(vehicle => vehicle.category === vehicleTab)
    .filter(vehicle => 
      searchTerm === '' || 
      vehicle.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const title = 'Wheel PCD Calculator | Pitch Circle Diameter Calculator South Africa';
  const description = 'Free wheel PCD (Pitch Circle Diameter) calculator. Calculate wheel bolt pattern measurements for BMW, Mercedes, Toyota, VW & more. Includes South African vehicle PCD database and popular wheel sizes.';
  const keywords = 'PCD calculator, pitch circle diameter, wheel bolt pattern, wheel PCD, mag wheel sizes, BMW PCD, Mercedes PCD, Toyota PCD, VW PCD, South Africa wheels, wheel fitment calculator';

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href="https://yoursite.com/online-automotive-calculators-and-tools/wheel-mag-pitch-circle-diameter-size-calculator" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="/images/pcd-calculator-og.jpg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="/images/pcd-calculator-og.jpg" />
      </Head>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6 flex items-center justify-center gap-4">
                <Target className="text-yellow-400" size={50} />
                Wheel PCD Calculator
              </h1>
              <p className="text-xl md:text-2xl mb-8 opacity-90">
                Calculate Pitch Circle Diameter (PCD) for perfect wheel fitment
              </p>
            <div className="mb-8">
        <button
          onClick={() => handleWheelQuoteRequest('PCD Calculator Wheel Quote Request')}
          className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-bold py-4 px-8 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center gap-3 mx-auto text-lg"
        >
          <ShoppingCart className="w-6 h-6" />
          Request for wheel / mags
        </button>
        <p className="text-blue-100 mt-2 text-sm">
          Get competitive quotes from verified wheel suppliers
        </p>
      </div>
      
              
              
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                  <Calculator size={16} />
                  Instant Calculations
                </span>
                <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                  <Car size={16} />
                  SA Vehicle Database
                </span>
                <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                  <CheckCircle size={16} />
                  Professional Accuracy
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          {/* Navigation Tabs */}
          <div className="flex flex-wrap justify-center mb-8 bg-white rounded-xl p-2 shadow-lg">
            <button
              onClick={() => setActiveTab('calculator')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'calculator'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              <Calculator className="inline mr-2" size={16} />
              PCD Calculator
            </button>
            <button
              onClick={() => setActiveTab('popular-sizes')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'popular-sizes'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              <Settings className="inline mr-2" size={16} />
              Popular Sizes
            </button>
            <button
              onClick={() => setActiveTab('vehicle-data')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'vehicle-data'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              <Car className="inline mr-2" size={16} />
              Vehicle Database
            </button>
          </div>

          {/* Calculator Tab */}
          {activeTab === 'calculator' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Calculator Input */}
                <div className="bg-white rounded-2xl p-8 shadow-xl">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <Ruler className="text-blue-600" />
                    PCD Calculator
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Number of Bolt Holes
                      </label>
                      <select
                        value={holes}
                        onChange={(e) => setHoles(parseInt(e.target.value))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-lg"
                        title="Select number of bolt holes"
                        aria-label="Number of bolt holes"
                      >
                        <option value={3}>3 holes</option>
                        <option value={4}>4 holes</option>
                        <option value={5}>5 holes</option>
                        <option value={6}>6 holes</option>
                        <option value={8}>8 holes</option>
                        <option value={10}>10 holes</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Measurement Type
                      </label>
                      <div className="grid grid-cols-1 gap-3">
                        <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-blue-300 transition-colors">
                          <input
                            type="radio"
                            name="measurementType"
                            value="center-to-center"
                            checked={measurementType === 'center-to-center'}
                            onChange={(e) => setMeasurementType(e.target.value as any)}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-semibold">Center to Center</div>
                            <div className="text-sm text-gray-600">
                              {holes % 2 === 0 ? 'Across the wheel diameter' : 'Between adjacent holes'}
                            </div>
                          </div>
                        </label>
                        <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-blue-300 transition-colors">
                          <input
                            type="radio"
                            name="measurementType"
                            value="edge-to-edge"
                            checked={measurementType === 'edge-to-edge'}
                            onChange={(e) => setMeasurementType(e.target.value as any)}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-semibold">Edge to Edge</div>
                            <div className="text-sm text-gray-600">Between hole edges</div>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Measurement (mm)
                      </label>
                      <input
                        type="number"
                        value={measurement}
                        onChange={(e) => setMeasurement(e.target.value)}
                        placeholder="Enter measurement in millimeters"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-lg"
                      />
                    </div>
                  </div>
                </div>

                {/* Results */}
                <div className="bg-white rounded-2xl p-8 shadow-xl">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <Target className="text-green-600" />
                    Calculation Result
                  </h2>

                  {result ? (
                    <div className="space-y-6">
                      <div className="text-center p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border-2 border-green-200">
                        <div className="text-4xl font-bold text-green-600 mb-2">
                          {result.pcd}mm
                        </div>
                        <div className="text-gray-700 font-semibold">
                          PCD: {holes}x{result.pcd}
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-xl">
                        <h4 className="font-semibold text-gray-900 mb-2">Calculation Formula:</h4>
                        <p className="text-sm text-gray-700">{result.formula}</p>
                      </div>
                       <div className="text-center">
        <button
          onClick={() => handleWheelQuoteRequest(`Wheel Quotes for ${holes}x${result.pcd} PCD`, {
            calculatedPCD: true,
            pcdValue: result.pcd,
            boltPattern: `${holes}x${result.pcd}`
          })}
          className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
        >
          <ShoppingCart className="w-5 h-5" />
          Get Quotes for {holes}x{result.pcd} Wheels
        </button>
      </div>

                      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <div className="flex items-start gap-3">
                          <Info className="text-blue-600 mt-1" size={16} />
                          <div className="text-sm text-blue-800">
                            <div className="font-semibold mb-1">Fitment Note:</div>
                            This PCD calculation helps ensure proper wheel fitment. Always verify with your vehicle's specifications and consult a professional for wheel installation.
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Calculator size={48} className="mx-auto mb-4 opacity-50" />
                      <p>Enter your measurements to calculate PCD</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Popular Sizes Tab */}
          {activeTab === 'popular-sizes' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-6xl mx-auto"
            >
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                  Popular Wheel Sizes & PCD Values
                </h2>
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-50 to-purple-50">
                        <th className="text-left py-4 px-6 font-bold text-gray-900 border-b-2 border-blue-200">Wheel Size</th>
                        <th className="text-left py-4 px-6 font-bold text-gray-900 border-b-2 border-blue-200">Common PCD Values</th>
                        <th className="text-left py-4 px-6 font-bold text-gray-900 border-b-2 border-blue-200">Center Bore Range</th>
                      </tr>
                    </thead>
                    <tbody>
                      {popularSizes.map((size, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="py-4 px-6 font-semibold text-blue-600 border-b border-gray-200">
                            {size.size}
                          </td>
                          <td className="py-4 px-6 border-b border-gray-200">
                            <div className="flex flex-wrap gap-2">
                              {size.commonPCD.map((pcd, pcdIndex) => (
                                <span key={pcdIndex} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                  {pcd}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="py-4 px-6 text-gray-700 border-b border-gray-200">
                            {size.centerBore}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {/* Vehicle Database Tab */}
          {activeTab === 'vehicle-data' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-6xl mx-auto"
            >
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                  South African Vehicle PCD Database
                </h2>

                {/* Vehicle Category Tabs */}
                <div className="flex flex-wrap justify-center mb-6 bg-gray-50 rounded-xl p-2">
                  <button
                    onClick={() => setVehicleTab('passenger')}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      vehicleTab === 'passenger'
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-600 hover:text-blue-600'
                    }`}
                  >
                    Passenger Cars
                  </button>
                  <button
                    onClick={() => setVehicleTab('suv')}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      vehicleTab === 'suv'
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-600 hover:text-blue-600'
                    }`}
                  >
                    SUVs
                  </button>
                  <button
                    onClick={() => setVehicleTab('commercial')}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      vehicleTab === 'commercial'
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-600 hover:text-blue-600'
                    }`}
                  >
                    Commercial
                  </button>
                  <button
                    onClick={() => setVehicleTab('luxury')}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      vehicleTab === 'luxury'
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-600 hover:text-blue-600'
                    }`}
                  >
                    Luxury
                  </button>
                </div>

                {/* Search */}
                <div className="relative mb-6 max-w-md mx-auto">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search make or model..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                  />
                </div>

                {/* Vehicle Table */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-50 to-purple-50">
                        <th className="text-left py-4 px-4 font-bold text-gray-900 border-b-2 border-blue-200">Make</th>
                        <th className="text-left py-4 px-4 font-bold text-gray-900 border-b-2 border-blue-200">Model</th>
                        <th className="text-left py-4 px-4 font-bold text-gray-900 border-b-2 border-blue-200">Year</th>
                        <th className="text-left py-4 px-4 font-bold text-gray-900 border-b-2 border-blue-200">PCD</th>
                        <th className="text-left py-4 px-4 font-bold text-gray-900 border-b-2 border-blue-200">Center Bore</th>
                        <th className="text-left py-4 px-4 font-bold text-gray-900 border-b-2 border-blue-200">Wheel Size</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredVehicles.map((vehicle, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4 font-semibold text-blue-600 border-b border-gray-200">
                            {vehicle.make}
                          </td>
                          <td className="py-3 px-4 border-b border-gray-200">
                            {vehicle.model}
                          </td>
                          <td className="py-3 px-4 text-gray-600 border-b border-gray-200">
                            {vehicle.year}
                          </td>
                          <td className="py-3 px-4 border-b border-gray-200">
                            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                              {vehicle.pcd}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-700 border-b border-gray-200">
                            {vehicle.centerBore}
                          </td>
                          <td className="py-3 px-4 text-gray-700 border-b border-gray-200">
                            {vehicle.wheelSize}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {/* SEO Content Section */}
          <CalculatorQuoteRequest
            toolId="wheel-mag-pitch-circle-diameter-size-calculator"
            title="Request multiple wheel and mag quotes"
            description="Request multiple quotes from different wheel and mag providers. Include your vehicle details, rim size and PCD measurements, choose your location, and attach photos of your wheels or markings in the upload step."
          />

          <div className="mt-16 max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                Understanding Pitch Circle Diameter (PCD)
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Info className="text-blue-600" />
                    What is PCD?
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Pitch Circle Diameter (PCD) is the diameter of an imaginary circle that passes through the center 
                    of all the bolt holes on a wheel. It's a critical measurement for ensuring proper wheel fitment 
                    and is typically expressed as the number of bolts followed by the diameter in millimeters 
                    (e.g., 5x114.3).
                  </p>
                  <p className="text-gray-700">
                    The PCD must match exactly between your vehicle's hub and the replacement wheel for proper 
                    installation and safe operation. Using wheels with incorrect PCD can lead to improper mounting, 
                    vibration, and potential safety hazards.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Calculator className="text-green-600" />
                    How is PCD Calculated?
                  </h3>
                  <div className="space-y-3 text-gray-700">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="font-semibold">For Even Number of Holes:</div>
                      <div className="text-sm">PCD = Distance across center (diameter)</div>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="font-semibold">For Odd Number of Holes:</div>
                      <div className="text-sm">PCD = Distance ÷ cos(180°/number of holes)</div>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <div className="font-semibold">Edge-to-Edge Method:</div>
                      <div className="text-sm">PCD = Distance ÷ sin(180°/number of holes)</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">When Do You Need PCD Measurements?</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Wheel Replacement:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="text-green-500" size={16} />
                        Upgrading to aftermarket wheels
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="text-green-500" size={16} />
                        Replacing damaged wheels
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="text-green-500" size={16} />
                        Seasonal tire changes
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Performance Modifications:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="text-green-500" size={16} />
                        Installing larger diameter wheels
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="text-green-500" size={16} />
                        Weight reduction with lightweight alloys
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="text-green-500" size={16} />
                        Custom wheel designs
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="text-center p-6 bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl text-white">
                <h3 className="text-xl font-bold mb-3">Need Professional Wheel Installation?</h3>
                <p className="mb-4 opacity-90">
                  Our calculator helps you find the right specifications, but professional installation ensures 
                  safety and optimal performance. Get quotes for wheels and installation services.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button 
                    onClick={() => handleWheelQuoteRequest('Professional Wheel Installation Service')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2 mx-auto"
                  >
                    <ShoppingCart size={16} />
                    Request Wheel Quotes
                  </button>
                  <Link href={getFullUrl("product")} className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2 mx-auto">
                    Purchase Wheels / Mags online
                    <ChevronRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
          </div>
      <DownloadApp />
        {(
        (isPopupOpen && popupType === 'carousel') || 
        localShowPopup
      ) && (
        <RequestItemFormPopup 
          currentPopupType={popupType || 'carousel'}
          isContextOpen={isPopupOpen || localShowPopup}
          onClose={handleClosePopup}
          initialData={getInitialData()}
        />
      )}
    </>
  );
};

export default WheelPCDCalculator;